import React, { useState, useRef, useEffect } from 'react';
import { Box as ChakraBox, Input as ChakraInput, Flex, Text } from '@chakra-ui/react';
import { Box } from '../UI-CHAKRA/Box';
import { FormErrorMessage } from '../UI-CHAKRA/FormErrorMessage';
import { CountryFlag } from '../CountryFlag/CountryFlag';
import { countriesData } from '../../shared/countries';


interface SelectCountryAutocompleteProps {
    value: string;
    onChange(val: string): void;
    error?: boolean;
    label?: string;
    placeholder?: string;
    errorMessage?: string;
}

function SelectCountryAutocomplete(props: SelectCountryAutocompleteProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredCountries, setFilteredCountries] = useState(countriesData);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const selectedCountry = countriesData.find((c) => c.id === props.value);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (searchTerm === '') {
            setFilteredCountries(countriesData);
        } else {
            const filtered = countriesData.filter((country) => country.name.toLowerCase().includes(searchTerm.toLowerCase()));
            setFilteredCountries(filtered);
        }
    }, [searchTerm]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setIsOpen(true);
    };

    const handleCountrySelect = (countryId: string) => {
        props.onChange(countryId);
        setIsOpen(false);
        setSearchTerm('');
        inputRef.current?.blur();
    };

    const handleInputFocus = () => {
        setIsOpen(true);
        setSearchTerm('');
    };

    const handleInputKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
            setSearchTerm('');
            inputRef.current?.blur();
        }
    };

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setTimeout(() => {
            if (wrapperRef.current && !wrapperRef.current.contains(document.activeElement)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        }, 200);
    };

    return (
        <ChakraBox position="relative" ref={wrapperRef} w="100%">
            {props.label && (
                <Box mb={'10px'}>
                    <Text fontWeight="bold" textTransform="uppercase" fontSize={'12px'}>
                        {props.label}
                    </Text>
                </Box>
            )}

            <ChakraBox position="relative" w="100%">
                <ChakraBox position="relative" w="100%">
                    <Flex
                        alignItems="center"
                        h={'45px'}
                        border={props.error ? "2px solid" : "1px solid"}
                        borderColor={props.error ? "error.main" : "#CBBFE6"}
                        borderRadius="10px"
                        px="12px"
                        cursor="pointer"
                        onClick={() => inputRef.current?.focus()}
                        position="relative"
                        bg="#fff"
                        w="100%"
                    >
                        {selectedCountry && !isOpen && (
                            <Box mr="8px">
                                <CountryFlag countryCode={selectedCountry.id} />
                            </Box>
                        )}
                        <ChakraInput
                            ref={inputRef}
                            flex={1}
                            h={'100%'}
                            border="none"
                            borderWidth="0"
                            outline="none"
                            p={0}
                            value={isOpen ? searchTerm : (selectedCountry ? selectedCountry.name : '')}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onKeyDown={handleInputKeyDown}
                            placeholder={props.placeholder || 'Selecione o país'}
                            autoComplete="off"
                            isInvalid={false}
                            _focus={{
                                boxShadow: 'none',
                                border: 'none',
                                borderWidth: '0',
                                outline: 'none'
                            }}
                            _hover={{
                                border: 'none',
                                borderWidth: '0'
                            }}
                            _active={{
                                border: 'none',
                                borderWidth: '0'
                            }}
                            _invalid={{
                                boxShadow: 'none',
                                border: 'none',
                                borderWidth: '0',
                                outline: 'none'
                            }}
                            bg="transparent"
                            cursor={'default'}
                        />
                        <ChakraBox pointerEvents="none" color="gray.700" ml="8px">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2 4L6 8L10 4" stroke="#4A5568" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </ChakraBox>
                    </Flex>
                </ChakraBox>
            </ChakraBox>

            {isOpen && (
                <Box
                    position="absolute"
                    top="100%"
                    left={0}
                    right={0}
                    mt={'4px'}
                    maxH="300px"
                    overflowY="auto"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    boxShadow="lg"
                    zIndex={1000}
                >
                    {filteredCountries.length === 0 ? (
                        <Box p={'12px'} color="gray.500">
                            Nenhum país encontrado
                        </Box>
                    ) : (
                        filteredCountries.map((country: any) => (
                            <Flex
                                fontFamily={'Ambit'}
                                fontSize={'14px'}
                                key={country.id}
                                p={'12px'}
                                cursor="pointer"
                                alignItems="center"
                                gap="8px"
                                _hover={{ bg: 'gray.100' }}
                                onClick={() => handleCountrySelect(country.id)}
                                bg={props.value === country.id ? 'purple.50' : 'transparent'}
                            >
                                <CountryFlag countryCode={country.id} width={24} height={16} />
                                {country.name}
                            </Flex>
                        ))
                    )}
                </Box>
            )}

            <FormErrorMessage fontWeight={'bold'} textTransform={'uppercase'}>
                {props.errorMessage}
            </FormErrorMessage>
        </ChakraBox>
    );
}

export default React.memo(SelectCountryAutocomplete);